import type { LocalMessage } from 'stream-chat';
import { mapChatMediaFilesForRender } from '@features/components/Chat/components/ChatContent/Messages/utils/common';
import {
  ClinicalDataAttachmentMedia,
  ClinicalDataShareMessage,
  LabeledEntries,
  OrganApprovalShareMessage,
  QuoteDataShareMessage,
} from '@features/components/Chat/components/ChatContent/Messages/types/chatMessages';
import { joinWithNewline } from '@/utils/helpers';

export const createTableData = (labeledEntries: LabeledEntries[] | null) => {
  return labeledEntries?.map(({ title: entryTitle, value }) => `${entryTitle}: ${value || '-'}`).join('\n') ?? '';
};

const getMediaComments = (mediaFiles?: ClinicalDataAttachmentMedia[]): string[] => {
  if (!mediaFiles?.length) {
    return [];
  }
  return mediaFiles.map((file: ClinicalDataAttachmentMedia, index: number) => {
    if (!file.details?.length) {
      return '';
    }
    if (mediaFiles.length === 1) {
      return `Media comment: ${file.details}`;
    }
    const mediaListIndex = index + 1;
    return `Media comment ${mediaListIndex}: ${file.details}`;
  });
};

export const processClinicalDataAttachment = (message: LocalMessage) => {
  const { title, subtitle, labeledEntries, media } = message.clinicalDataAttachment as ClinicalDataShareMessage;
  const tableData = createTableData(labeledEntries);

  return {
    ...message,
    text: joinWithNewline(title, subtitle, tableData, ...getMediaComments(media)),
    attachments: mapChatMediaFilesForRender(media),
  };
};

export const processQuoteAttachment = (message: LocalMessage) => {
  const { title, labeledEntries } = message.quoteAttachment as QuoteDataShareMessage;
  const tableData = createTableData(labeledEntries);

  return {
    ...message,
    text: joinWithNewline(title, tableData),
  };
};

export const processOrganApprovalAttachment = (message: LocalMessage) => {
  const { title } = message.organApprovalAttachment as OrganApprovalShareMessage;

  return {
    ...message,
    text: title,
  };
};

export const renderMessage = (message: LocalMessage) => {
  if (message.clinicalDataAttachment) {
    return processClinicalDataAttachment(message);
  }
  if (message.quoteAttachment) {
    return processQuoteAttachment(message);
  }
  if (message.organApprovalAttachment) {
    return processOrganApprovalAttachment(message);
  }
  return message;
};
